import React, { useState, ChangeEvent, useMemo } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  IconButton,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

// Types
interface PersonData {
  id: number;
  lastName: string;
  firstName: string;
  ageCategory: string;
  gender: string;
  arrivalDate: Dayjs | null;
  departureDate: Dayjs | null;
  numberOfBreakfasts: string;
  numberOfDinners: string;
}

// Constants
const AGE_CATEGORIES = ["Child (1-12)", "Teen (13-17)", "Adult (18+)"];
const GENDERS = ["Male", "Female"];
const LINEN_RATE = 10.0;
const LODGING_RATE = 25.0;
const BREAKFAST_RATE_ADULT = 10.0;
const BREAKFAST_RATE_CHILD = 5.0;
const DINNER_RATE_ADULT = 21.0;
const DINNER_RATE_CHILD = 10.0;

function getBreakfastRate(ageCategory: string): number {
  if (ageCategory.includes("Adult") || ageCategory.includes("Teen")) return BREAKFAST_RATE_ADULT;
  return BREAKFAST_RATE_CHILD;
}

function getDinnerRate(ageCategory: string): number {
  if (ageCategory.includes("Adult") || ageCategory.includes("Teen")) return DINNER_RATE_ADULT;
  return DINNER_RATE_CHILD;
}

function getNights(arrivalDate: Dayjs | null, departureDate: Dayjs | null): number {
  if (!arrivalDate || !departureDate) return 0;
  const diff = departureDate.diff(arrivalDate, 'day');
  return diff > 0 ? diff : 0;
}

export default function SkiClubBookingForm() {
  const [members, setMembers] = useState<PersonData[]>([
    {
      id: 1,
      lastName: '',
      firstName: '',
      ageCategory: 'Adult (18+)',
      gender: 'Male',
      arrivalDate: null,
      departureDate: null,
      numberOfBreakfasts: '',
      numberOfDinners: '',
    },
  ]);

  const [guests, setGuests] = useState<PersonData[]>([]);
  const [payByVenmo, setPayByVenmo] = useState<boolean>(false);
  const [couplesRoom, setCouplesRoom] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);

  const handleInputChange = (id: number, field: keyof PersonData, isMember: boolean) => (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    const updater = isMember ? setMembers : setGuests;
    updater((prev) =>
      prev.map((person) =>
        person.id === id ? { ...person, [field]: value.replace(/^0+/, '') || '' } : person
      )
    );
  };

  const handleSelectChange = (id: number, field: keyof PersonData, isMember: boolean) => (
    event: SelectChangeEvent<string>
  ) => {
    const { value } = event.target;
    const updater = isMember ? setMembers : setGuests;
    updater((prev) =>
      prev.map((person) => (person.id === id ? { ...person, [field]: value } : person))
    );
  };

  const handleDateChange = (id: number, field: keyof PersonData, date: Dayjs | null, isMember: boolean) => {
    const updater = isMember ? setMembers : setGuests;
    updater((prev) =>
      prev.map((person) => (person.id === id ? { ...person, [field]: date } : person))
    );
  };

  const addMember = () => {
    setMembers((prev) => [
      ...prev,
      {
        id: Date.now(),
        lastName: '',
        firstName: '',
        ageCategory: 'Adult (18+)',
        gender: 'Male',
        arrivalDate: null,
        departureDate: null,
        numberOfBreakfasts: '',
        numberOfDinners: '',
      },
    ]);
  };

  const addGuest = () => {
    setGuests((prev) => [
      ...prev,
      {
        id: Date.now(),
        lastName: '',
        firstName: '',
        ageCategory: 'Adult (18+)',
        gender: 'Female',
        arrivalDate: null,
        departureDate: null,
        numberOfBreakfasts: '',
        numberOfDinners: '',
      },
    ]);
  };

  const deletePerson = (id: number, isMember: boolean) => {
    const updater = isMember ? setMembers : setGuests;
    updater((prev) => prev.filter((person) => person.id !== id));
  };

  function getPersonCosts(person: PersonData) {
    const nights = getNights(person.arrivalDate, person.departureDate);
    const breakfasts = Number(person.numberOfBreakfasts) || 0;
    const dinners = Number(person.numberOfDinners) || 0;

    const linenFee = nights > 0 ? LINEN_RATE : 0;
    const lodgingFee = nights * LODGING_RATE;
    const breakfastRate = getBreakfastRate(person.ageCategory);
    const breakfastFee = breakfasts * breakfastRate;
    const dinnerRate = getDinnerRate(person.ageCategory);
    const dinnerFee = dinners * dinnerRate;
    const total = linenFee + lodgingFee + breakfastFee + dinnerFee;

    return {
      nights,
      linenFee,
      lodgingFee,
      breakfasts,
      breakfastRate,
      breakfastFee,
      dinners,
      dinnerRate,
      dinnerFee,
      total
    };
  }

  const bookingTotal = useMemo(() => {
    const membersTotal = members.reduce((acc, m) => acc + getPersonCosts(m).total, 0);
    const guestsTotal = guests.reduce((acc, g) => acc + getPersonCosts(g).total, 0);
    return membersTotal + guestsTotal;
  }, [members, guests]);

  const venmoFee = useMemo(() => {
    if (!payByVenmo) return 0;
    const increments = Math.ceil(bookingTotal / 50);
    return increments > 0 ? increments : 0;
  }, [bookingTotal, payByVenmo]);

  const totalWithFees = bookingTotal + venmoFee;

  const handleSubmit = () => {
    // Log data or send to server as needed
    console.log("Members:", members);
    console.log("Guests:", guests);
    console.log("Pay by Venmo:", payByVenmo);
    console.log("Couples Room:", couplesRoom);
    console.log("Total Booking:", bookingTotal);
    console.log("Venmo Fee:", venmoFee);
    console.log("Total Amount Due:", totalWithFees);

    // Show confirmation dialog
    setShowDialog(true);
  };

  const allPersons = [
    ...members.map((m) => ({ ...m, type: 'Member' })),
    ...guests.map((g) => ({ ...g, type: 'Guest' }))
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 4, maxWidth: 800 }}>
        <Typography variant="h4">Monmouth Ski Club Bookings Form</Typography>
        
        <Typography variant="h6" sx={{ mt: 3 }}>Members</Typography>
        {members.map((member) => (
          <Box key={member.id} sx={{ mb: 2, border: '1px solid gray', p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1">Member #{member.id}</Typography>
              <IconButton onClick={() => deletePerson(member.id, true)} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
            <TextField
              label="Last Name"
              value={member.lastName}
              onChange={handleInputChange(member.id, 'lastName', true)}
              fullWidth sx={{ mb: 1 }}
            />
            <TextField
              label="First Name"
              value={member.firstName}
              onChange={handleInputChange(member.id, 'firstName', true)}
              fullWidth sx={{ mb: 1 }}
            />
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel>Age Category</InputLabel>
              <Select value={member.ageCategory} onChange={handleSelectChange(member.id, 'ageCategory', true)}>
                {AGE_CATEGORIES.map((age) => (
                  <MenuItem key={age} value={age}>{age}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel>Gender</InputLabel>
              <Select value={member.gender} onChange={handleSelectChange(member.id, 'gender', true)}>
                {GENDERS.map((g) => (
                  <MenuItem key={g} value={g}>{g}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <DatePicker
              label="Arrival Date"
              value={member.arrivalDate}
              onChange={(newValue) => handleDateChange(member.id, 'arrivalDate', newValue, true)}
              sx={{ mb: 1, width: '100%' }}
            />
            <DatePicker
              label="Departure Date"
              value={member.departureDate}
              onChange={(newValue) => handleDateChange(member.id, 'departureDate', newValue, true)}
              sx={{ mb: 1, width: '100%' }}
            />
            <TextField
              type="number"
              label="Number of Breakfasts"
              value={member.numberOfBreakfasts}
              onChange={handleInputChange(member.id, 'numberOfBreakfasts', true)}
              fullWidth sx={{ mb: 1 }}
            />
            <TextField
              type="number"
              label="Number of Dinners"
              value={member.numberOfDinners}
              onChange={handleInputChange(member.id, 'numberOfDinners', true)}
              fullWidth
            />
          </Box>
        ))}
        <Button variant="contained" onClick={addMember} sx={{ mb: 3 }}>
          Add Member
        </Button>

        <Typography variant="h6">Guests</Typography>
        {guests.map((guest) => (
          <Box key={guest.id} sx={{ mb: 2, border: '1px solid gray', p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1">Guest #{guest.id}</Typography>
              <IconButton onClick={() => deletePerson(guest.id, false)} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
            <TextField
              label="Last Name"
              value={guest.lastName}
              onChange={handleInputChange(guest.id, 'lastName', false)}
              fullWidth sx={{ mb: 1 }}
            />
            <TextField
              label="First Name"
              value={guest.firstName}
              onChange={handleInputChange(guest.id, 'firstName', false)}
              fullWidth sx={{ mb: 1 }}
            />
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel>Age Category</InputLabel>
              <Select value={guest.ageCategory} onChange={handleSelectChange(guest.id, 'ageCategory', false)}>
                {AGE_CATEGORIES.map((age) => (
                  <MenuItem key={age} value={age}>{age}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel>Gender</InputLabel>
              <Select value={guest.gender} onChange={handleSelectChange(guest.id, 'gender', false)}>
                {GENDERS.map((g) => (
                  <MenuItem key={g} value={g}>{g}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <DatePicker
              label="Arrival Date"
              value={guest.arrivalDate}
              onChange={(newValue) => handleDateChange(guest.id, 'arrivalDate', newValue, false)}
              sx={{ mb: 1, width: '100%' }}
            />
            <DatePicker
              label="Departure Date"
              value={guest.departureDate}
              onChange={(newValue) => handleDateChange(guest.id, 'departureDate', newValue, false)}
              sx={{ mb: 1, width: '100%' }}
            />
            <TextField
              type="number"
              label="Number of Breakfasts"
              value={guest.numberOfBreakfasts}
              onChange={handleInputChange(guest.id, 'numberOfBreakfasts', false)}
              fullWidth sx={{ mb: 1 }}
            />
            <TextField
              type="number"
              label="Number of Dinners"
              value={guest.numberOfDinners}
              onChange={handleInputChange(guest.id, 'numberOfDinners', false)}
              fullWidth
            />
          </Box>
        ))}
        <Button variant="contained" onClick={addGuest} sx={{ mt: 1 }}>
          Add Guest
        </Button>



        {/* Couples checkbox below add guest */}
        <Box sx={{ mt: 3 }}>
            <FormControlLabel
            control={<Checkbox checked={couplesRoom} onChange={(e) => setCouplesRoom(e.target.checked)} />}
            label="Check if you would like a 'Couples' room if available"
            sx={{ mt: 3 }}
            />
        </Box>

        <FormControlLabel
          control={<Checkbox checked={payByVenmo} onChange={(e) => setPayByVenmo(e.target.checked)} />}
          label="Pay by Venmo"
        />

        <Typography variant="h6" sx={{ mt: 3 }}>Cost Summary</Typography>

        {allPersons.map((person) => {
          const costs = getPersonCosts(person);
          const fullName = `${person.lastName}, ${person.firstName}`;
          return (
            <Box key={person.id} sx={{ border: '1px solid #ccc', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle1">
                {person.type}: {fullName}
              </Typography>
              <Typography>Nights: {costs.nights}</Typography>
              {costs.linenFee > 0 && (
                <Typography>Linen Fee: ${costs.linenFee.toFixed(2)}</Typography>
              )}
              <Typography>
                Lodging Fee: ${costs.lodgingFee.toFixed(2)} ({costs.nights} nights x ${LODGING_RATE})
              </Typography>
              <Typography>
                Breakfasts: {costs.breakfasts} ( {costs.breakfasts} x ${costs.breakfastRate.toFixed(2)} = ${costs.breakfastFee.toFixed(2)} )
              </Typography>
              <Typography>
                Dinners: {costs.dinners} ( {costs.dinners} x ${costs.dinnerRate.toFixed(2)} = ${costs.dinnerFee.toFixed(2)} )
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Person Total: ${costs.total.toFixed(2)}
              </Typography>
            </Box>
          );
        })}

        <Typography>Total Booking (without Venmo fee): ${bookingTotal.toFixed(2)}</Typography>
        {payByVenmo && <Typography>Venmo Fee: ${venmoFee.toFixed(2)}</Typography>}
        <Typography variant="h5">Total Due: ${totalWithFees.toFixed(2)}</Typography>

        <Button variant="contained" color="primary" sx={{ mt: 3 }} onClick={handleSubmit}>
          Submit Booking
        </Button>

        {/* Confirmation Modal */}
        <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
          <DialogTitle>Booking Submitted</DialogTitle>
          <DialogContent>
            <Typography>Your booking has been submitted successfully!</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => setShowDialog(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}
